import * as React from 'react'
import { DayPicker, type ChevronProps, type DayButtonProps } from 'react-day-picker'
import 'react-day-picker/style.css'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Figma (node 301:1181 — Calendar):
 *
 * Basic (node 17921:45929):
 *   w-[214px], bg-background, border border-border, rounded-lg (10px), p-2 (8px)
 *   gap-4 (16px) between nav header and month grid
 *   Day cells: 28x28 (default) or 38x38 (md), rounded-lg (10px)
 *   Day headers: text-xs text-muted-foreground, w-[28px] h-5
 *   Nav arrows: w-7 h-7 rounded-lg, hover:bg-accent
 *   Month/Year: text-sm font-medium, centered
 *   Rows gap: gap-2 (8px)
 *
 * Presets (node 21203:53693):
 *   w-[300px], rounded-xl (14px), pt-3, day cells 38x38 (md size)
 *
 * Day states:
 *   Default:    text-foreground
 *   Outside:    text-muted-foreground
 *   Today:      bg-muted text-foreground
 *   Selected:   bg-primary text-primary-foreground
 *   Range mid:  bg-accent text-accent-foreground rounded-none
 *   Range start: bg-primary text-primary-foreground rounded-l-lg
 *   Range end:   bg-primary text-primary-foreground rounded-r-lg
 *   Disabled:   text-muted-foreground opacity-50
 *   Hover:      bg-accent
 */

export type IntuiCalendarSize = 'default' | 'md'

export type IntuiCalendarProps = React.ComponentProps<typeof DayPicker> & {
  /** Day cell size: default (28px) or md (38px) */
  size?: IntuiCalendarSize
  /** Remove border and background (for embedding inside a card) */
  bare?: boolean
  /** Stack months vertically for mobile layout */
  mobile?: boolean
  /** Custom day cell content renderer */
  renderDay?: (date: Date, formattedDay: string) => React.ReactNode
}

const sizeConfig = {
  default: {
    vars: [
      '[--rdp-day-height:28px]',
      '[--rdp-day-width:28px]',
      '[--rdp-day_button-height:28px]',
      '[--rdp-day_button-width:28px]',
    ].join(' '),
    cellClass: 'w-7 h-7',
    headerClass: 'w-7 h-5',
    buttonRadius: '[--rdp-day_button-border-radius:10px]',
  },
  md: {
    vars: [
      '[--rdp-day-height:38px]',
      '[--rdp-day-width:38px]',
      '[--rdp-day_button-height:38px]',
      '[--rdp-day_button-width:38px]',
    ].join(' '),
    cellClass: 'w-[38px] h-[38px]',
    headerClass: 'w-[38px] h-5',
    buttonRadius: '[--rdp-day_button-border-radius:10px]',
  },
}

// Stable Chevron — extracted to module level to avoid re-creating on every render
function CalendarChevron({ orientation }: ChevronProps) {
  return orientation === 'left' ? (
    <ChevronLeft className="h-4 w-4" />
  ) : (
    <ChevronRight className="h-4 w-4" />
  )
}

function IntuiCalendar({
  className,
  size = 'default',
  bare = false,
  mobile = false,
  renderDay,
  showOutsideDays = true,
  captionLayout,
  dir,
  classNames: externalClassNames,
  components: externalComponents,
  ...props
}: IntuiCalendarProps) {
  const cfg = sizeConfig[size]
  const isDropdown = captionLayout === 'dropdown'

  // Stable DayButton component to prevent remounting on every render
  const CustomDayButton = React.useMemo(() => {
    if (!renderDay) return undefined
    return function CustomDay({ day, modifiers: _modifiers, className: dayClassName, ...dayProps }: DayButtonProps) {
      return (
        <button className={dayClassName} {...dayProps}>
          {renderDay(day.date, String(day.date.getDate()))}
        </button>
      )
    }
  }, [renderDay])

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      captionLayout={captionLayout}
      dir={dir}
      className={cn(
        // Container — tight fit, no extra space
        !bare && 'bg-background border border-border rounded-lg',
        'p-2 overflow-hidden',
        // Style rdp dropdown selects — compact with inline chevron
        '[&_select]:text-sm [&_select]:font-medium [&_select]:bg-transparent [&_select]:border-none [&_select]:cursor-pointer [&_select]:focus:outline-none',
        "[&_select]:bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2024%2024%22%20fill%3D%22none%22%20stroke%3D%22currentColor%22%20stroke-width%3D%222%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%3E%3Cpath%20d%3D%22m6%209%206%206%206-6%22%2F%3E%3C%2Fsvg%3E')] [&_select]:bg-no-repeat [&_select]:bg-[right_0px_center] [&_select]:pr-4 [&_select]:appearance-none",
        // CSS variables for day cell sizing
        '[--rdp-accent-color:hsl(var(--primary))]',
        '[--rdp-accent-background-color:hsl(var(--primary))]',
        '[--rdp-selected-border:none]',
        '[--rdp-today-color:hsl(var(--foreground))]',
        cfg.vars,
        cfg.buttonRadius,
        className,
      )}
      classNames={{
        // Month caption — centered label
        month_caption: 'flex items-center justify-center h-7',
        caption_label: cn('text-sm font-medium text-foreground', isDropdown && 'sr-only'),
        // Navigation — absolute within months container, overlays month_caption row
        nav: 'absolute top-0 inset-x-0 flex items-center justify-between z-10 pointer-events-none h-7 px-0',
        button_previous: cn(
          'inline-flex items-center justify-center rounded-lg pointer-events-auto',
          'w-7 h-7',
          'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
          'disabled:opacity-50 disabled:pointer-events-none',
        ),
        button_next: cn(
          'inline-flex items-center justify-center rounded-lg pointer-events-auto',
          'w-7 h-7',
          'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
          'disabled:opacity-50 disabled:pointer-events-none',
        ),
        // Month layout — relative for nav positioning, flex-col for mobile
        months: cn('relative flex gap-4 overflow-hidden', mobile && 'flex-col'),
        month: 'flex flex-col gap-4 overflow-hidden',
        month_grid: 'border-collapse',
        // Day headers
        weekdays: 'flex',
        weekday: cn(
          'text-muted-foreground text-xs font-normal',
          cfg.headerClass,
          'flex items-center justify-center',
        ),
        // Week rows — tight spacing
        week: 'flex mt-0.5',
        // Day cells
        day: cn('relative flex items-center justify-center text-sm p-0', cfg.cellClass),
        day_button: cn(
          'inline-flex items-center justify-center rounded-lg text-sm cursor-pointer',
          cfg.cellClass,
          'hover:bg-accent hover:text-accent-foreground',
          'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring',
        ),
        // States
        selected: 'bg-primary text-primary-foreground rounded-lg hover:bg-primary hover:text-primary-foreground',
        today: 'bg-muted text-foreground rounded-lg',
        outside: 'text-muted-foreground',
        disabled: 'text-muted-foreground opacity-50 pointer-events-none',
        hidden: 'invisible',
        // Range
        range_start: 'bg-primary text-primary-foreground rounded-l-lg rounded-r-none',
        range_end: 'bg-primary text-primary-foreground rounded-r-lg rounded-l-none',
        range_middle: 'bg-accent text-accent-foreground rounded-none',
        // Week numbers
        week_number: 'text-xs text-muted-foreground w-8 flex items-center justify-center',
        // Dropdown selectors — compact to fit inside calendar width
        dropdowns: 'flex gap-1 items-center justify-center',
        dropdown: 'text-sm font-medium bg-transparent border-none focus:outline-none cursor-pointer appearance-none',
        dropdown_root: 'relative inline-flex items-center',
        months_dropdown: 'text-sm font-medium bg-transparent border-none focus:outline-none cursor-pointer appearance-none pr-4',
        years_dropdown: 'text-sm font-medium bg-transparent border-none focus:outline-none cursor-pointer appearance-none pr-1 w-[60px]',
        ...externalClassNames,
      }}
      components={{
        Chevron: CalendarChevron,
        ...(CustomDayButton ? { DayButton: CustomDayButton } : {}),
        ...externalComponents,
      }}
      {...props}
    />
  )
}
IntuiCalendar.displayName = 'IntuiCalendar'

export { IntuiCalendar }
